'use client';
import { useRef } from 'react';
import LabelToTraining from './label-to-training';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useGSAP } from '@gsap/react';
import initAnimation from './animations/safeguarding-privacy-animation';
import { useLocale } from '@/components/providers/context/locale-context';
import { JContainer } from '@/components/ui/layout';

type SafeguardingPrivacyItemProps = {
    id: string;
    title: string;
    description: string;
};

function SafeguardingPrivacyItem({
    id,
    title,
    description,
}: Readonly<SafeguardingPrivacyItemProps>) {
    return (
        <div
            id={`safeguardingPrivacyItem-${id}`}
            className="flex flex-col gap-[12px] sm:gap-[16px]"
        >
            <h3
                className={clsx('j-h5 hidden text-[#0D0D10] sm:block')}
                style={{ height: '3em', lineHeight: '1.5em' }}
            >
                {title}
            </h3>
            <div className={clsx('j-h5 block text-[#0D0D10] sm:hidden')}>
                {title}
            </div>
            <div className={clsx('j-t2 text-[#0D0D1099]')}>{description}</div>
        </div>
    );
}

function SafeguardingPrivacy() {
    const { t } = useTranslation('safeguarding-privacy');
    const safeguardingPrivacyItems: SafeguardingPrivacyItemProps[] = t(
        'items',
        { returnObjects: true },
    ) as SafeguardingPrivacyItemProps[];

    const rootRef = useRef<HTMLDivElement>(null);
    const safeguardingPrivacyRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const labelToTrainingRef = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            initAnimation(
                rootRef.current!,
                containerRef.current!,
                safeguardingPrivacyRef.current!,
                labelToTrainingRef.current!,
            );
        },
        { scope: rootRef },
    );

    return (
        <section
            id="safeguarding-privacy"
            ref={rootRef}
            className="relative flex h-auto w-full max-w-full flex-col items-center bg-white"
        >
            <JContainer
                size="lg"
                padding="sm"
                className="z-0 flex flex-col gap-[40px] sm:gap-[60px]"
            >
                <div
                    ref={safeguardingPrivacyRef}
                    className="flex w-full flex-col gap-[40px] sm:gap-[60px]"
                >
                    <div className="mt-[60px] flex w-full flex-col gap-[24px] sm:mt-[120px] sm:flex-row sm:gap-[80px]">
                        <h2 className={clsx('j-h2 w-full text-left sm:w-2/3 ')}>
                            <div
                                style={{
                                    background:
                                        'linear-gradient(90deg, #6B48FF -0.07%, #E7A9FD 99.93%)',
                                    WebkitTextFillColor: 'transparent',
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                }}
                            >
                                {t('title')}
                            </div>
                            <div>{t('subtitle')}</div>
                        </h2>
                        <div
                            className={clsx(
                                'flex w-full items-end justify-end sm:w-1/3',
                                'j-h5',
                            )}
                        >
                            {t('more')}
                        </div>
                    </div>

                    <div className="h-[1px] w-full bg-[#9D9D9D]" />

                    <div
                        ref={containerRef}
                        className="mb-[120px] grid grid-cols-1 gap-x-0 gap-y-[60px] sm:grid-cols-3 sm:gap-x-[40px]"
                    >
                        {safeguardingPrivacyItems.map((item, index) => (
                            <div
                                id={item.id}
                                key={item.id}
                                className="safeguarding-privacy-item"
                            >
                                <SafeguardingPrivacyItem
                                    key={item.id}
                                    {...item}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </JContainer>

            <div
                id="label-to-training-container"
                ref={labelToTrainingRef}
                className="z-10 h-[var(--simulaix-vh,100svh)] w-full"
            >
                <LabelToTraining />
            </div>
        </section>
    );
}

export default SafeguardingPrivacy;
